package com.karawek.core.ui.theme

import androidx.compose.ui.graphics.Color

data class CoreColorScheme(
    val background: Color,
    val onBackground: Color,
    val onBackgroundStrong: Color,

    val surface: Color,
    val surfaceVariant: Color,
    val surfaceSunken: Color,  // pressed/inset surfaces (list row tap, sunken fields)

    val textPrimary: Color,
    val textSecondary: Color,
    val textMuted: Color,

    val divider: Color,
    val borderStrong: Color,   // stronger borders (field borders, section dividers)

    val disabledFill: Color,
    val disabledInk: Color,

    val primary: Color,
    val onPrimary: Color,

    val secondary: Color,
    val onSecondary: Color,

    val error: Color,
    val onError: Color,

    val infoSurface: Color,
    val infoOutline: Color,
    val infoAccent: Color,

    val warningSurface: Color,
    val warningOutline: Color,
    val warningAccent: Color,

    val errorSurface: Color,
    val errorOutline: Color,
    val errorAccent: Color,
)

// Light mode — matches Claude Design System token values
val CoreLightColorScheme = CoreColorScheme(
    // Backgrounds  --bg-grouped / --bg-base
    background = Color(0xFFEDF1EF),
    onBackground = Color(0xFF6B7A74),
    onBackgroundStrong = Color(0xFF1F2A27),

    // Surfaces  --surface-card / --surface-raised / --surface-sunken
    surface = Color(0xFFFFFFFF),
    surfaceVariant = Color(0xFFFFFFFF),
    surfaceSunken = Color(0xFFF4F7F5),

    // Text  --text-primary / --text-secondary / --text-tertiary
    textPrimary = Color(0xFF1F2A27),
    textSecondary = Color(0xFF6B7A74),
    textMuted = Color(0xFF8A968F),

    // Borders  --border-hairline / --border-strong
    divider = Color(0xFFE3E8E5),
    borderStrong = Color(0xFFC9D0CC),

    // Disabled  --disabled-fill / --disabled-ink
    disabledFill = Color(0xFFD9DEDB),
    disabledInk = Color(0xFF8A968F),

    // Primary accent — pastel blue fill / navy ink  --accent / --accent-ink
    primary = Color(0xFFAECBF5),
    onPrimary = Color(0xFF2E4B7A),

    // Secondary — seedling green  --secondary / --secondary-ink
    secondary = Color(0xFF6FBE5E),
    onSecondary = Color(0xFF3F8A45),

    // Error
    error = Color(0xFFD06C6C),
    onError = Color(0xFFFFFFFF),

    // Info — soft blue  --accent-soft / --border-hairline / --accent-strong
    infoSurface = Color(0xFFE7EFFB),
    infoOutline = Color(0xFFCDDFF8),
    infoAccent = Color(0xFF5B8DEF),

    // Warning / Pending — warm sand, never red/orange  --pending-soft / / --pending
    warningSurface = Color(0xFFF6EEDD),
    warningOutline = Color(0xFFEEDDBB),
    warningAccent = Color(0xFFB58A4A),

    // Error surfaces
    errorSurface = Color(0xFFFFE9E9),
    errorOutline = Color(0xFFF3CACA),
    errorAccent = Color(0xFFD06C6C),
)

// Dark mode — matches Claude Design System token values  [data-theme="dark"]
val CoreDarkColorScheme = CoreColorScheme(
    // Backgrounds  --bg-grouped / --bg-base
    background = Color(0xFF16201D),
    onBackground = Color(0xFF9BA5A0),
    onBackgroundStrong = Color(0xFFF2F4F3),

    // Surfaces  --surface-card / --surface-raised / --surface-sunken
    surface = Color(0xFF23292A),
    surfaceVariant = Color(0xFF2B3230),
    surfaceSunken = Color(0xFF1C1C1E),

    // Text  --text-primary / --text-secondary / --text-tertiary
    textPrimary = Color(0xFFF2F4F3),
    textSecondary = Color(0xFF9BA5A0),
    textMuted = Color(0xFF7C8681),

    // Borders  --border-hairline / --border-strong
    divider = Color(0xFF38403C),
    borderStrong = Color(0xFF495250),

    // Disabled  --disabled-fill / --disabled-ink
    disabledFill = Color(0xFF2B3230),
    disabledInk = Color(0xFF6B736F),

    // Primary accent — pastel blue / dark navy  --accent / --accent-ink
    primary = Color(0xFF86AEEF),
    onPrimary = Color(0xFF12203F),

    // Secondary — seedling green  --secondary / --secondary-ink
    secondary = Color(0xFF8BCD77),
    onSecondary = Color(0xFFA8D897),

    // Error
    error = Color(0xFFCF6A6A),
    onError = Color(0xFF1A0F10),

    // Info — soft blue  --accent-soft / / --accent-strong
    infoSurface = Color(0xFF17233A),
    infoOutline = Color(0xFF243656),
    infoAccent = Color(0xFF86AEEF),

    // Warning / Pending — warm sand  --pending-soft / / --pending
    warningSurface = Color(0xFF2C2618),
    warningOutline = Color(0xFF4A3E28),
    warningAccent = Color(0xFFC79A4E),

    // Error surfaces
    errorSurface = Color(0xFF2E1A1A),
    errorOutline = Color(0xFF5A2C2C),
    errorAccent = Color(0xFFCF6A6A),
)
